// src/services/userAPI.ts
// ✅ LOGGING: Complete migration to centralized logging system
// ✅ ENVIRONMENT: Using environment configuration

import { authService } from './authService';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from './configService';
import { 
  User, 
  CreateUserRequest, 
  UpdateUserRequest, 
  UserFilters, 
  UserListResponse, 
  BulkUploadResult,
  BulkUploadUser,
  UserHistory
} from '../types/user';

// ✅ LOGGING: Create dedicated logger for User API
const userAPILogger = logger.createServiceLogger('UserAPI');

// Export UserStats interface for other components
export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  total: number;        // Added for compatibility
  active: number;       // Added for compatibility  
  inactive: number;     // Added for compatibility
  byRank: Record<string, number>;
  byAssignment: Record<string, number>;
  byDepartment: Record<string, number>;
}

class UserAPI {
  private baseUrl: string;

  constructor() {
    // Get base URL from environment configuration
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * ✅ UPDATED: Simplified request method using authService with structured logging
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const timer = logger.startTimer('UserAPI', 'makeRequest');
    const url = `${this.baseUrl}${endpoint}`;
    
    userAPILogger.info('Making user API request', {
      endpoint,
      method: options.method || 'GET',
      hasBody: !!options.body,
      timestamp: new Date().toISOString()
    });
    
    try {
      // ✅ Use new auth service - automatically gets fresh token
      const response = await authService.makeAuthenticatedRequest(url, options);

      userAPILogger.info('User API response received', {
        endpoint,
        status: response.status,
        statusText: response.statusText
      });

      if (!response.ok) {
        const errorText = await response.text();
        userAPILogger.error('User API request failed', {
          endpoint,
          status: response.status,
          statusText: response.statusText,
          errorText: errorText.substring(0, 500) // Limit error text length
        });
        
        if (response.status === 404) {
          throw new Error(`API endpoint not found: ${endpoint}`);
        } else if (response.status === 403) {
          throw new Error(`Access denied. Check your permissions.`);
        } else if (response.status === 401) {
          throw new Error(`Authentication failed. Please log in again.`);
        } else {
          const errorData = JSON.parse(errorText || '{}');
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }
      }

      const responseText = await response.text();
      userAPILogger.debug('User API response body received', {
        endpoint,
        responseLength: responseText.length,
        hasContent: !!responseText
      });
      
      if (!responseText) {
        // For empty responses, return appropriate empty values based on expected type
        timer();
        return {} as T;
      }
      
      const parsedResponse = JSON.parse(responseText) as T;
      timer();
      return parsedResponse;

    } catch (error: any) {
      timer();
      userAPILogger.error('User API request error', {
        endpoint,
        method: options.method || 'GET',
        error: error.message,
        errorCode: error.code
      }, error);
      
      // Handle auth-specific errors from authService
      if (error.code === 'NO_SESSION') {
        throw new Error('Please log in again');
      }
      
      if (error.code === 'TOKEN_EXPIRED') {
        throw new Error('Session expired, please refresh');
      }

      if (error.code === 'NETWORK_ERROR') {
        throw new Error(`Network error: Cannot connect to API at ${url}. Check if the API is running and CORS is configured.`);
      }
      
      throw error;
    }
  }

  // Get paginated list of users with filtering
  async getUsers(
    page: number = 1,
    pageSize: number = 50,
    filters?: UserFilters
  ): Promise<UserListResponse> {
    const timer = logger.startTimer('UserAPI', 'getUsers');
    
    const params = new URLSearchParams({
      limit: pageSize.toString(),
    });

    if (filters) {
      if (filters.employmentStatus?.length === 1) {
        params.append('status', filters.employmentStatus[0]);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
    }

    userAPILogger.info('Fetching users with filters', {
      page,
      pageSize,
      hasFilters: !!filters,
      searchTerm: filters?.search || null,
      statusFilter: filters?.employmentStatus?.[0] || null
    });

    const response = await this.makeRequest<{
      data: {
        users: User[];
        lastEvaluatedKey: string | null;
        count: number;
        total: number;
      }
    }>(`/users?${params}`);

    const data = response.data || response;
    
    userAPILogger.info('Users fetched successfully', {
      userCount: data.users?.length || 0,
      totalCount: data.total || data.count || 0,
      hasNextPage: !!data.lastEvaluatedKey,
      page
    });
    
    timer();
    return {
      users: data.users || [],
      totalCount: data.total || data.count || 0,
      page: page,
      pageSize: pageSize,
      hasNextPage: !!data.lastEvaluatedKey
    };
  }

  // Get single user by ID
  async getUser(userId: string): Promise<User> {
    userAPILogger.info('Fetching single user', { userId });
    const response = await this.makeRequest<{ data: User }>(`/users/${userId}`);
    userAPILogger.debug('User fetched successfully', { userId });
    return response.data || response;
  }

  // Create new user
  async createUser(userData: CreateUserRequest): Promise<User> {
    userAPILogger.info('Creating new user', {
      employeeNumber: userData.employeeNumber,
      firstName: userData.firstName,
      lastName: userData.lastName,
      rank: userData.rank,
      department: userData.department,
      email: userData.email
    });
    
    const response = await this.makeRequest<{ data: User }>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    userAPILogger.info('User created successfully', {
      employeeNumber: userData.employeeNumber,
      fullName: `${userData.firstName} ${userData.lastName}`
    });
    
    return response.data || response;
  }

  // Update existing user
  async updateUser(userId: string, userData: UpdateUserRequest): Promise<User> {
    userAPILogger.info('Updating user', {
      userId,
      updateFields: Object.keys(userData),
      hasPersonalInfo: !!(userData.firstName || userData.lastName),
      hasJobInfo: !!(userData.rank || userData.department)
    });
    
    const response = await this.makeRequest<{ data: User }>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    
    userAPILogger.info('User updated successfully', {
      userId,
      updateCount: Object.keys(userData).length
    });
    
    return response.data || response;
  }

  // Update user employment status
  async updateUserStatus(
    userId: string, 
    status: string, 
    reason?: string
  ): Promise<User> {
    // ✅ Get tenantId from auth service instead of localStorage
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || '';

    userAPILogger.info('Updating user employment status', {
      userId,
      newStatus: status,
      reason: reason || 'No reason provided',
      tenantId
    });

    const response = await this.makeRequest<{ data: User }>(`/users/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({
        employmentStatus: status,
        reason,
        tenantId: tenantId,
      }),
    });
    
    userAPILogger.info('User status updated successfully', {
      userId,
      newStatus: status
    });
    
    return response.data || response;
  }

  // Bulk update user statuses
  async bulkUpdateStatus(
    userIds: string[],
    status: string,
    reason?: string
  ): Promise<{ updated: string[]; errors: Array<{ userId: string; error: string }> }> {
    // ✅ Get tenantId from auth service instead of localStorage
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || '';

    userAPILogger.info('Starting bulk user status update', {
      userCount: userIds.length,
      targetStatus: status,
      reason: reason || 'No reason provided',
      tenantId,
      userIds: userIds.slice(0, 5) // Log first 5 IDs for debugging
    });

    const response = await this.makeRequest<{
      data: { updated: string[]; errors: Array<{ userId: string; error: string }> }
    }>('/users/bulk-status', {
      method: 'PATCH',
      body: JSON.stringify({
        userIds,
        employmentStatus: status,
        reason,
        tenantId: tenantId,
      }),
    });
    
    const result = response.data || response;
    
    userAPILogger.info('Bulk status update completed', {
      totalUsers: userIds.length,
      successfulUpdates: result.updated.length,
      failedUpdates: result.errors.length,
      successRate: `${((result.updated.length / userIds.length) * 100).toFixed(1)}%`
    });
    
    return result;
  }

  // Delete user (soft delete)
  async deleteUser(userId: string, reason?: string): Promise<void> {
    // ✅ Get tenantId from auth service instead of localStorage
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || '';

    userAPILogger.info('Deleting user (soft delete)', {
      userId,
      reason: reason || 'No reason provided',
      tenantId
    });

    await this.makeRequest(`/users/${userId}`, {
      method: 'DELETE',
      body: JSON.stringify({
        reason,
        tenantId: tenantId,
      }),
    });
    
    userAPILogger.info('User deleted successfully', { userId });
  }

  // Get user history
  async getUserHistory(userId: string): Promise<UserHistory[]> {
    // ✅ Get tenantId from auth service instead of localStorage
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || '';

    userAPILogger.info('Fetching user history', { userId, tenantId });

    const response = await this.makeRequest<{ data: UserHistory[] }>(`/users/${userId}/history?tenantId=${tenantId}`);
    
    const history = response.data || response;
    userAPILogger.debug('User history fetched', {
      userId,
      historyCount: Array.isArray(history) ? history.length : 0
    });
    
    return history;
  }

  // Bulk upload users
  async bulkUploadUsers(users: BulkUploadUser[]): Promise<BulkUploadResult> {
    // ✅ Get tenantId from auth service instead of localStorage
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || '';

    userAPILogger.info('Starting bulk user upload', {
      userCount: users.length,
      tenantId,
      sampleUser: users[0] ? {
        employeeNumber: users[0].employeeNumber,
        firstName: users[0].firstName,
        lastName: users[0].lastName
      } : null
    });

    const response = await this.makeRequest<{ data: BulkUploadResult }>('/users/bulk-upload', {
      method: 'POST',
      body: JSON.stringify({
        users,
        tenantId: tenantId,
      }),
    });
    
    const result = response.data || response;
    
    // ✅ FIXED: Use correct property names from BulkUploadResult type
    userAPILogger.info('Bulk upload completed', {
      totalUsers: users.length,
      totalProcessed: result.totalProcessed || 0,
      successCount: result.successCount || 0,
      errorCount: result.errorCount || 0,
      duplicateCount: result.duplicateCount || 0
    });
    
    return result;
  }

  // Check for duplicate users
  async checkDuplicates(
    employeeNumber: string,
    badgeNumber: string,
    excludeUserId?: string
  ): Promise<{ employeeNumberExists: boolean; badgeNumberExists: boolean }> {
    // ✅ Get tenantId from auth service instead of localStorage
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || '';

    const params = new URLSearchParams({
      employeeNumber,
      badgeNumber,
      tenantId: tenantId,
    });

    if (excludeUserId) {
      params.append('excludeUserId', excludeUserId);
    }

    userAPILogger.debug('Checking for duplicate users', {
      employeeNumber,
      badgeNumber,
      excludeUserId: excludeUserId || null,
      tenantId
    });

    const response = await this.makeRequest<{ 
      data: { employeeNumberExists: boolean; badgeNumberExists: boolean }
    }>(`/users/check-duplicates?${params}`);
    
    const result = response.data || response;
    
    userAPILogger.debug('Duplicate check completed', {
      employeeNumber,
      badgeNumber,
      employeeNumberExists: result.employeeNumberExists,
      badgeNumberExists: result.badgeNumberExists
    });
    
    return result;
  }

  // Export users to CSV
  async exportUsers(filters?: UserFilters): Promise<Blob> {
    // ✅ Get tenantId from auth service instead of localStorage
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || '';

    const params = new URLSearchParams({
      tenantId: tenantId,
    });

    if (filters) {
      if (filters.employmentStatus?.length) {
        params.append('employmentStatus', filters.employmentStatus.join(','));
      }
      if (filters.ranks?.length) {
        params.append('ranks', filters.ranks.join(','));
      }
      if (filters.assignments?.length) {
        params.append('assignments', filters.assignments.join(','));
      }
      if (filters.departments?.length) {
        params.append('departments', filters.departments.join(','));
      }
      if (filters.shifts?.length) {
        params.append('shifts', filters.shifts.join(','));
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
    }

    userAPILogger.info('Exporting users to CSV', {
      tenantId,
      hasFilters: !!filters,
      filterCount: filters ? Object.keys(filters).length : 0
    });

    // ✅ Use authService for authenticated download
    const response = await authService.makeAuthenticatedRequest(`${this.baseUrl}/users/export?${params}`);

    if (!response.ok) {
      userAPILogger.error('User export failed', {
        status: response.status,
        statusText: response.statusText
      });
      throw new Error(`Export failed: ${response.statusText}`);
    }

    userAPILogger.info('Users exported successfully');
    return response.blob();
  }

  // Get sample CSV template
  async getSampleCSV(): Promise<Blob> {
    // ✅ Get tenantId from auth service instead of localStorage
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || '';

    userAPILogger.info('Downloading sample CSV template', { tenantId });

    // ✅ Use authService for authenticated download
    const response = await authService.makeAuthenticatedRequest(`${this.baseUrl}/users/sample-csv?tenantId=${tenantId}`);

    if (!response.ok) {
      userAPILogger.error('Sample CSV download failed', {
        status: response.status,
        statusText: response.statusText
      });
      throw new Error(`Sample CSV download failed: ${response.statusText}`);
    }

    userAPILogger.info('Sample CSV downloaded successfully');
    return response.blob();
  }

  // Upload user photo
  async uploadUserPhoto(userId: string, file: File): Promise<{ photoUrl: string }> {
    // ✅ Get tenantId from auth service instead of localStorage
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || '';

    userAPILogger.info('Uploading user photo', {
      userId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      tenantId
    });

    const formData = new FormData();
    formData.append('photo', file);
    formData.append('userId', userId);
    formData.append('tenantId', tenantId);

    // ✅ Use authService for authenticated upload (without Content-Type for FormData)
    const token = await authService.getToken();
    const response = await fetch(`${this.baseUrl}/users/${userId}/photo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Don't set Content-Type for FormData - let browser set it with boundary
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      userAPILogger.error('User photo upload failed', {
        userId,
        fileName: file.name,
        status: response.status,
        error: errorData.message
      });
      throw new Error(errorData.message || `Photo upload failed: ${response.statusText}`);
    }

    const result = await response.json();
    userAPILogger.info('User photo uploaded successfully', {
      userId,
      fileName: file.name,
      photoUrl: result.photoUrl
    });

    return result;
  }

  // Delete user photo
  async deleteUserPhoto(userId: string): Promise<void> {
    // ✅ Get tenantId from auth service instead of localStorage
    const tokenInfo = authService.getLastTokenInfo();
    const tenantId = tokenInfo?.tenantId || '';

    userAPILogger.info('Deleting user photo', { userId, tenantId });

    await this.makeRequest(`/users/${userId}/photo`, {
      method: 'DELETE',
      body: JSON.stringify({
        tenantId: tenantId,
      }),
    });
    
    userAPILogger.info('User photo deleted successfully', { userId });
  }

  // Get user statistics
  async getUserStats(): Promise<UserStats> {
    const timer = logger.startTimer('UserAPI', 'getUserStats');
    
    try {
      userAPILogger.info('Calculating user statistics');
      
      const userResponse = await this.getUsers(1, 1000);
      const users = userResponse.users;
      
      const activeCount = users.filter(u => u.employmentStatus === 'Active').length;
      const inactiveCount = users.filter(u => 
        u.employmentStatus === 'Inactive' || 
        u.employmentStatus === 'Terminated' || 
        u.employmentStatus === 'Retired' || 
        u.employmentStatus === 'Leave of Absence'
      ).length;
      
      const stats: UserStats = {
        totalUsers: users.length,
        activeUsers: activeCount,
        inactiveUsers: inactiveCount,
        total: users.length,        // Compatibility field
        active: activeCount,        // Compatibility field
        inactive: inactiveCount,    // Compatibility field
        byRank: {} as Record<string, number>,
        byAssignment: {} as Record<string, number>,
        byDepartment: {} as Record<string, number>
      };

      users.forEach(user => {
        const rank = user.rank || 'Unknown';
        const assignment = user.assignment || 'Unknown';
        const department = user.department || 'Unknown';
        
        stats.byRank[rank] = (stats.byRank[rank] || 0) + 1;
        stats.byAssignment[assignment] = (stats.byAssignment[assignment] || 0) + 1;
        if (user.department) {
          stats.byDepartment[department] = (stats.byDepartment[department] || 0) + 1;
        }
      });

      userAPILogger.info('User statistics calculated', {
        totalUsers: stats.totalUsers,
        activeUsers: stats.activeUsers,
        inactiveUsers: stats.inactiveUsers,
        rankDistribution: Object.keys(stats.byRank).length,
        assignmentDistribution: Object.keys(stats.byAssignment).length,
        departmentDistribution: Object.keys(stats.byDepartment).length
      });

      timer();
      return stats;
    } catch (error) {
      timer();
      userAPILogger.error('Error calculating user stats', {
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      
      return {
        totalUsers: 0,
        activeUsers: 0,
        inactiveUsers: 0,
        total: 0,
        active: 0,
        inactive: 0,
        byRank: {},
        byAssignment: {},
        byDepartment: {}
      };
    }
  }

  // ========== ASSIGNMENT-RELATED METHODS ==========

  // Get officer's assigned equipment
  async getOfficerEquipment(userId: string): Promise<any[]> {
    const timer = logger.startTimer('UserAPI', 'getOfficerEquipment');
    
    try {
      userAPILogger.info('Fetching officer equipment', { userId });
      
      // Dynamic import to avoid circular dependencies
      const assignmentModule = await import('./assignmentAPI');
      const assignmentAPI = assignmentModule.assignmentAPI;
      
      // Use your existing API to get officer equipment
      const response = await assignmentAPI.getOfficerEquipment(userId);
      
      if (response.success && response.data) {
        userAPILogger.info('Officer equipment loaded', {
          userId,
          equipmentCount: response.data.assignments.length
        });
        timer();
        return response.data.assignments;
      } else {
        userAPILogger.warn('No equipment found for officer', { userId });
        timer();
        return [];
      }
      
    } catch (error) {
      timer();
      userAPILogger.error('Error loading officer equipment', {
        userId,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      return [];
    }
  }

  // Check if user has any assigned equipment (for deletion prevention)
  async hasAssignedEquipment(userId: string): Promise<boolean> {
    try {
      const equipment = await this.getOfficerEquipment(userId);
      const hasEquipment = equipment.length > 0;
      
      userAPILogger.debug('Checked user equipment assignment', {
        userId,
        hasEquipment,
        equipmentCount: equipment.length
      });
      
      return hasEquipment;
    } catch (error) {
      userAPILogger.error('Error checking assigned equipment', {
        userId,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      return false;
    }
  }

  // Get equipment summary for an officer
  async getOfficerEquipmentSummary(userId: string): Promise<{
    totalItems: number;
    lastAssignmentDate?: string;
  }> {
    try {
      userAPILogger.debug('Getting officer equipment summary', { userId });
      
      const assignments = await this.getOfficerEquipment(userId);
      
      const summary = {
        totalItems: assignments.length,
        lastAssignmentDate: undefined as string | undefined
      };
      
      // Find most recent assignment date
      if (assignments.length > 0) {
        const mostRecent = assignments.reduce((latest: any, assignment: any) => {
          const assignmentDate = new Date(assignment.assignmentDate);
          return assignmentDate > latest ? assignmentDate : latest;
        }, new Date(0));
        
        if (mostRecent > new Date(0)) {
          summary.lastAssignmentDate = mostRecent.toISOString();
        }
      }
      
      userAPILogger.debug('Officer equipment summary calculated', {
        userId,
        totalItems: summary.totalItems,
        hasLastAssignment: !!summary.lastAssignmentDate
      });
      
      return summary;
      
    } catch (error) {
      userAPILogger.error('Error loading officer equipment summary', {
        userId,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      
      return {
        totalItems: 0
      };
    }
  }
}

export const userAPI = new UserAPI();
